import { t } from "@airange/shared/i18n-identity-picker";
import { getIdentityStatusDisplay } from "@airange/shared/identity-status";
import { type ProColumns, ProTable } from "@ant-design/pro-components";
import { Tag } from "antd";
import type { ReactNode } from "react";
import { ADMIN_ACTION_COLUMN_WIDTH_COMPACT } from "./admin-action-width";
import { formatAdminDateTime } from "./admin-list-columns";
import { buildAdminTableToolBarRender } from "./admin-page-actions";
import { AdminRowActions } from "./admin-row-actions";
import { AdminListScaffold, buildAdminProTableCommonProps } from "./admin-table";
import { IdentityUserText } from "./identity-user-text";
import type { SsoOrganizationPickerUser } from "./sso-organization-user-picker-types";

export type SsoSelectedUserPreviewTableProps = {
  users: SsoOrganizationPickerUser[];
  persistenceKey: string;
  title?: ReactNode;
  emptyText?: ReactNode;
  nameColumnTitle?: ReactNode;
  pickMembersLabel?: ReactNode;
  showUpdatedAt?: boolean;
  onPickMembers?: () => void;
  onRemoveUser?: (user: SsoOrganizationPickerUser) => void;
};

export function SsoSelectedUserPreviewTable({
  users,
  persistenceKey,
  title = t("common.identityPicker.selectedUsersTitle"),
  emptyText = t("common.identityPicker.selectedUsersEmpty"),
  nameColumnTitle = t("common.fields.displayName"),
  pickMembersLabel = t("common.identityPicker.pickMembersFromOrganization"),
  showUpdatedAt = true,
  onPickMembers,
  onRemoveUser,
}: SsoSelectedUserPreviewTableProps) {
  const columns: ProColumns<SsoOrganizationPickerUser>[] = [
    {
      title: nameColumnTitle,
      dataIndex: "display_name",
      render: (_value, record) => (
        <IdentityUserText
          displayText={record.display_name}
          userId={record.id}
          username={record.username}
          fullName={record.display_name}
          detailLabels={{
            userId: t("common.fields.userId"),
            username: t("common.fields.username"),
            fullName: t("common.fields.displayName"),
          }}
        />
      ),
    },
    {
      title: t("common.fields.username"),
      dataIndex: "username",
      render: (_value, record) => (
        <IdentityUserText
          userId={record.id}
          username={record.username}
          fullName={record.display_name}
          detailLabels={{
            userId: t("common.fields.userId"),
            username: t("common.fields.username"),
            fullName: t("common.fields.displayName"),
          }}
        />
      ),
    },
    {
      title: t("common.fields.organization"),
      dataIndex: "organization_name",
    },
    {
      title: t("common.fields.organizationUnit"),
      key: "organization_unit_names",
      render: (_value, record) => record.organization_unit_names.join(" / ") || "-",
    },
    {
      title: t("common.fields.phone"),
      dataIndex: "phone",
      render: (_value, record) => record.phone || "-",
    },
    {
      title: t("common.nouns.status"),
      dataIndex: "status",
      render: (_value, record) => {
        const statusDisplay = getIdentityStatusDisplay(record.status);
        return <Tag color={statusDisplay.tone}>{statusDisplay.label}</Tag>;
      },
    },
    ...(showUpdatedAt
      ? ([
          {
            title: t("common.fields.updatedAt"),
            dataIndex: "updated_at",
            render: (_value: unknown, record: SsoOrganizationPickerUser) =>
              formatAdminDateTime(record.updated_at),
          },
        ] satisfies ProColumns<SsoOrganizationPickerUser>[])
      : []),
    {
      title: t("common.fields.actions"),
      key: "actions",
      width: ADMIN_ACTION_COLUMN_WIDTH_COMPACT,
      render: (_value, record) => (
        <AdminRowActions
          actions={[
            {
              key: "remove",
              semantic: "delete",
              label: t("common.identityPicker.actions.remove"),
              danger: true,
              disabled: !onRemoveUser,
              onClick: () => {
                onRemoveUser?.(record);
              },
            },
          ]}
        />
      ),
    },
  ];

  return (
    <AdminListScaffold
      table={
        <ProTable<SsoOrganizationPickerUser>
          rowKey="id"
          search={false}
          pagination={false}
          headerTitle={title}
          toolBarRender={
            onPickMembers
              ? buildAdminTableToolBarRender([
                  {
                    key: "pick-members",
                    intent: "create",
                    label: pickMembersLabel,
                    onClick: onPickMembers,
                  },
                ])
              : () => []
          }
          columns={columns}
          dataSource={users}
          tableLayout="auto"
          scroll={{ x: "max-content" }}
          tableAlertRender={false}
          tableAlertOptionRender={false}
          locale={{ emptyText }}
          {...buildAdminProTableCommonProps<SsoOrganizationPickerUser>({
            persistenceKey,
          })}
        />
      }
    />
  );
}
